import * as THREE from 'three';

export type ClothCollider = { center: THREE.Vector3; radius: number };

const tmpA = new THREE.Vector3();
const tmpB = new THREE.Vector3();
const tmpSide = new THREE.Vector3();
const tmpN = new THREE.Vector3();
const MAX_SPEED = 6.5;

/** A hanging strip of cloth simulated with Verlet integration (scarf and sash tails). */
export class ClothRibbon {
  readonly mesh: THREE.Mesh;
  private readonly count: number;
  private readonly segLength: number;
  private readonly pos: THREE.Vector3[] = [];
  private readonly prev: THREE.Vector3[] = [];
  private readonly positions: Float32Array;
  private readonly normals: Float32Array;
  private readonly geometry: THREE.BufferGeometry;
  private initialized = false;
  private time = Math.random() * 10;

  constructor(
    segments: number,
    length: number,
    private readonly width: number,
    material: THREE.Material,
    private readonly taper = 0.6,
    private readonly drag = 3.2,
    private readonly flap = 1,
  ) {
    this.count = segments + 1;
    this.segLength = length / segments;
    for (let i = 0; i < this.count; i++) {
      this.pos.push(new THREE.Vector3());
      this.prev.push(new THREE.Vector3());
    }
    this.positions = new Float32Array(this.count * 2 * 3);
    this.normals = new Float32Array(this.count * 2 * 3);
    const uvs = new Float32Array(this.count * 2 * 2);
    const indices: number[] = [];
    for (let i = 0; i < this.count; i++) {
      uvs.set([0, i / segments, 1, i / segments], i * 4);
      if (i < segments) {
        const a = i * 2;
        indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
      }
    }
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage));
    this.geometry.setAttribute('normal', new THREE.BufferAttribute(this.normals, 3).setUsage(THREE.DynamicDrawUsage));
    this.geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    this.geometry.setIndex(indices);
    this.mesh = new THREE.Mesh(this.geometry, material);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = true;
  }

  reset(anchor: THREE.Vector3, hang: THREE.Vector3): void {
    for (let i = 0; i < this.count; i++) {
      this.pos[i].copy(anchor).addScaledVector(hang, i * this.segLength);
      this.prev[i].copy(this.pos[i]);
    }
    this.initialized = true;
  }

  update(
    dt: number,
    anchor: THREE.Vector3,
    hang: THREE.Vector3,
    sideRef: THREE.Vector3,
    windVelocity: THREE.Vector3,
    colliders: ClothCollider[],
  ): void {
    if (!this.initialized) this.reset(anchor, hang);
    if (dt <= 1e-5) {
      this.writeMesh(sideRef);
      return;
    }
    const steps = 3;
    const h = Math.min(dt, 1 / 30) / steps;
    this.time += dt;
    for (let s = 0; s < steps; s++) {
      for (let i = 1; i < this.count; i++) {
        const p = this.pos[i];
        const q = this.prev[i];
        let vx = (p.x - q.x) / h;
        let vy = (p.y - q.y) / h;
        let vz = (p.z - q.z) / h;
        // Cap the speed so a spin or flip swings the cloth instead of flinging it out flat.
        const sp = Math.hypot(vx, vy, vz);
        if (sp > MAX_SPEED) {
          const k = MAX_SPEED / sp;
          vx *= k;
          vy *= k;
          vz *= k;
        }
        const k = i / (this.count - 1);
        // Flag-like flutter perpendicular to the strip, stronger toward the free end.
        const flutter = Math.sin(this.time * 11 + i * 0.9) * 1.6 * this.flap * k * (0.3 + windVelocity.length() * 0.12);
        const ax = (windVelocity.x - vx) * this.drag + sideRef.x * flutter;
        const ay = -9.8 + (windVelocity.y - vy) * this.drag + Math.cos(this.time * 7 + i) * flutter * 0.4;
        const az = (windVelocity.z - vz) * this.drag + sideRef.z * flutter;
        q.copy(p);
        p.x += vx * h * 0.995 + ax * h * h;
        p.y += vy * h * 0.995 + ay * h * h;
        p.z += vz * h * 0.995 + az * h * h;
      }
      this.pos[0].copy(anchor);
      this.prev[0].copy(anchor);
      for (let iter = 0; iter < 5; iter++) {
        // Root stiffness keeps the first segment hanging from the knot.
        tmpA.copy(anchor).addScaledVector(hang, this.segLength);
        this.pos[1].lerp(tmpA, 0.35);
        for (let i = 0; i < this.count - 1; i++) {
          const a = this.pos[i];
          const b = this.pos[i + 1];
          tmpB.subVectors(b, a);
          const d = tmpB.length() || 1e-6;
          const diff = (d - this.segLength) / d;
          if (i === 0) {
            b.addScaledVector(tmpB, -diff);
          } else {
            a.addScaledVector(tmpB, diff * 0.5);
            b.addScaledVector(tmpB, -diff * 0.5);
          }
        }
        for (let i = 1; i < this.count; i++) {
          const p = this.pos[i];
          for (const c of colliders) {
            tmpB.subVectors(p, c.center);
            const d = tmpB.length();
            if (d < c.radius && d > 1e-6) p.addScaledVector(tmpB, (c.radius - d) / d);
          }
        }
      }
    }
    this.writeMesh(sideRef);
  }

  private writeMesh(sideRef: THREE.Vector3): void {
    for (let i = 0; i < this.count; i++) {
      const p = this.pos[i];
      const next = this.pos[Math.min(i + 1, this.count - 1)];
      const prev = this.pos[Math.max(i - 1, 0)];
      tmpA.subVectors(next, prev).normalize();
      tmpSide.crossVectors(tmpA, sideRef).cross(tmpA).normalize();
      if (tmpSide.lengthSq() < 1e-6) tmpSide.set(1, 0, 0);
      const k = i / (this.count - 1);
      const half = (this.width * (1 - k * (1 - this.taper))) / 2;
      tmpN.crossVectors(tmpSide, tmpA).normalize();
      const o = i * 6;
      this.positions[o] = p.x - tmpSide.x * half;
      this.positions[o + 1] = p.y - tmpSide.y * half;
      this.positions[o + 2] = p.z - tmpSide.z * half;
      this.positions[o + 3] = p.x + tmpSide.x * half;
      this.positions[o + 4] = p.y + tmpSide.y * half;
      this.positions[o + 5] = p.z + tmpSide.z * half;
      this.normals.set([tmpN.x, tmpN.y, tmpN.z, tmpN.x, tmpN.y, tmpN.z], o);
    }
    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.normal.needsUpdate = true;
    this.geometry.computeBoundingSphere();
  }
}
